import React from 'react';
import { Lock, AlertCircle } from 'lucide-react';

/**
 * Password Change Modal Component
 */
const PasswordModal = ({
  show,
  mustChange,
  passwordForm,
  setPasswordForm,
  passwordError,
  changingPassword,
  onSubmit,
  onClose
}) => {
  if (!show) return null;

  const handleChange = (field) => (e) => {
    setPasswordForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Change Your Password</h2>
          <p className="text-slate-600 mt-2">
            {mustChange 
              ? 'Please change your default password.' 
              : 'Enter current password and choose a new one.'}
          </p>
        </div>

        {passwordError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-700 text-sm">{passwordError}</p>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Current Password
            </label>
            <input
              type="password"
              value={passwordForm.current}
              onChange={handleChange('current')}
              required
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={passwordForm.new}
              onChange={handleChange('new')}
              required
              minLength={8}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={passwordForm.confirm}
              onChange={handleChange('confirm')}
              required
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div className="flex gap-3 pt-4">
            {!mustChange && (
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border-2 border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={changingPassword}
              className={`${mustChange ? 'w-full' : 'flex-1'} bg-slate-900 text-white py-3 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50`}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
          {mustChange && (
            <button
              type="button"
              onClick={onClose}
              data-testid="password-modal-later-btn"
              className="w-full text-center text-sm text-slate-400 hover:text-slate-600 pt-1"
            >
              Remind me later
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default PasswordModal;
